/**
 * Terrain rendering: palette-indexed, GPU-coloured.
 *
 * The central performance decision of the whole project lives here. The CPU
 * **never writes a pixel colour.** It writes material ids into a per-chunk
 * R16UI texture, uploading only the chunk's dirty rectangle, and the fragment
 * shader does all colouring by looking the id up in a palette texture.
 *
 * Consequences that fall out of that one choice:
 *   - a chunk that changed 4 cells uploads 4 cells, not 16384
 *   - recolouring the entire world (biome grade, frozen tint, void desaturation)
 *     costs one palette swap and zero CPU work
 *   - temperature glow, per-cell grain, and wetness are shader-side, so they're
 *     free at the CPU budget level
 *
 * Two textures per chunk: material ids and temperature. Both are integer
 * textures sampled with texelFetch — no filtering, which is correct for pixel
 * art and cheaper besides.
 */

import { profiler } from '../core/profiler';
import { M, materialCount } from '../sim/materials';
import { GRAIN_SPAN, type GrainSource } from '../sim/rigid';
import {
  CHUNK_SHIFT,
  CHUNK_SIZE,
  RENDER_TILES_PER_AXIS,
  RENDER_TILE_AXIS_SHIFT,
  RENDER_TILE_SIZE,
  type CellWorld,
  type Chunk,
} from '../sim/world';
import type { Camera } from './camera';
import { GLContext, Uniforms, createProgram } from './gl';
import { createUnlitTexture, type LightTexture } from './light';

/**
 * Rigid-body grain windows the shader will consider per fragment — issue #121.
 *
 * Eight, which is **not** `MAX_AWAKE`. The window has to follow a body that is
 * asleep as well as one in flight, or the speckle pops at the moment a crate
 * settles (see `RigidBodies.grainWindows`), and a room may hold more sleeping
 * bodies than the four the *upload* cap allows to move at once. Eight is two
 * rooms' worth of crates and doors on screen together, it is a uniform array
 * and a 5 KB texture rather than anything that scales with the world, and the
 * loop exits on the first window a fragment falls inside.
 *
 * A ninth visible body does not break: it keeps the world-hashed grain it has
 * today. Selection is in registration order so the set is stable frame to
 * frame rather than reshuffling as the camera drifts.
 */
const MAX_GRAIN_SLOTS = 8;

const VERT = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
uniform vec2 uChunkOrigin;   // world-space top-left of this chunk
uniform vec2 uCamera;        // world-space camera centre
uniform vec2 uViewSize;      // world units visible
out vec2 vCell;
out vec2 vWorld;
void main() {
  // aPos is a unit quad in [-1,1]; map to [0,CHUNK] cell space.
  vec2 unit = aPos * 0.5 + 0.5;
  vCell = unit * float(${CHUNK_SIZE});
  vec2 world = uChunkOrigin + vCell;
  vWorld = world;
  vec2 ndc = (world - uCamera) / (uViewSize * 0.5);
  gl_Position = vec4(ndc.x, -ndc.y, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
precision highp usampler2D;
precision highp isampler2D;

in vec2 vCell;
in vec2 vWorld;
out vec4 outColor;

// Also declared in the vertex stage. Linking shares it, and the fragment
// stage needs the *exact* integer world cell rather than a varying that has
// been through interpolation.
uniform vec2 uChunkOrigin;

uniform usampler2D uMat;
uniform isampler2D uTemp;
uniform usampler2D uFlags;
uniform sampler2D uPalette;      // 1 row, one texel per material
uniform sampler2D uMatProps;     // 1 row: r=grain g=emissive b=alpha a=opacity
uniform float uTime;
uniform vec3 uGrade;             // biome colour grade multiplier
uniform float uLodPressure;

// Rigid-body grain windows (#121/#42). uBodyCount is 0 when no solver is
// supplied, and the loop below is then not entered at all — which is what
// makes the no-source image bit-for-bit the pre-#121 one.
uniform usampler2D uBodyGrain;
uniform int uBodyCount;
uniform vec2 uBodyOrigin[${MAX_GRAIN_SLOTS}];

// Light field (§6.3). uLightGain is 0 and uAmbient is 1 when no field is
// supplied, which reproduces the pre-field image exactly.
uniform sampler2D uLight;
uniform vec2 uLightOrigin;       // world cell at the window's top-left
uniform vec2 uLightScale;        // 1 / window extent, in world cells
uniform vec2 uLightTexSize;      // texel dimensions of the current window
uniform float uAmbient;
uniform float uLightGain;

// Issue #150. uLight is already LINEAR-filtered (see render/light.ts), so a
// plain texture() fetch is bilinear across the eighth-chunk tile grid already
// — but bilinear is only C0 continuous, and its slope kinks visibly at every
// texel boundary, which is the "large squares" complaint: a single emitter's
// falloff is built from flat-shaded facets, not a curve.
//
// This reshapes the interpolant rather than adding taps. Given the texel grid
// coordinate, replace the linear fractional part with the smoothstep curve
// (3f² - 2f³, C1 continuous, zero slope at each texel centre) and let the
// *same* hardware LINEAR sampler blend between the two texels either side —
// one texture fetch, same bind, same bandwidth, no seam introduced, and it is
// exactly a no-op at every texel centre (f=0 or f=1 map to themselves) so the
// field's actual values are untouched, only how they're read between them.
vec3 sampleLight(vec2 world) {
  vec2 uv = (world - uLightOrigin) * uLightScale;
  vec2 texel = uv * uLightTexSize - 0.5;
  vec2 i = floor(texel);
  vec2 f = texel - i;
  f = f * f * (3.0 - 2.0 * f);
  vec2 smoothUv = (i + 0.5 + f) / uLightTexSize;
  return texture(uLight, smoothUv).rgb;
}

// Stable per-cell hash. Must match core/rng.ts hash2 closely enough that the
// grain doesn't crawl when a chunk is re-uploaded.
float hash(ivec2 p) {
  uint h = uint(p.x) * 0x27d4eb2du ^ uint(p.y) * 0x165667b1u;
  h ^= h >> 15; h *= 0x2c1b3c6du; h ^= h >> 12;
  return float(h & 0xffffffu) / 16777215.0;
}

// ------------------------------------------------------ sub-cell detail (#149)
//
// grainAt below is keyed on the *integer* world cell — one hash per sim cell,
// because it must agree with the rigid-body grain window's per-cell byte
// (#121). That makes it the wrong tool for sub-cell texture: the sim cell is
// the unit of *matter*, but nothing says the unit of *appearance* has to be
// the same size, and #149 (RENDER_SCALE, #146) asks for detail *inside* one
// cell. So this is a second, separate hash rather than a wider one — grainAt
// itself is untouched, and uBodyGrain / uBodyOrigin keep resolving per whole
// cell exactly as tests/rigidGrain.test.ts and tests/terrainGrainUpload.test.ts
// require.
//
// Keyed on vWorld, the float world-space varying (not vCell, which is
// chunk-local), so the sub-cell texture is anchored to the world and does not
// swim as the camera pans or as a chunk streams in under a fixed viewport.
//
// DETAIL_SUBCELLS=4: four buckets per axis per world cell is enough to read as
// texture rather than as a second, larger grain — finer subdivisions cost
// nothing extra (still one hash, no texture fetch) but stopped being visibly
// different past 4 in practice, so 4 is the number that buys the readable
// effect for the least output entropy.
//
// DETAIL_STRENGTH=0.04: bounded against docs/03 §7's legibility floor (34
// redmean at the nearest pair reachable in one biome; src/world/lighting.ts
// notes that floor lands near a rendered luminance of 0.58, worst pair ~20).
// The existing grain term above already spends part of that margin — a Monte
// Carlo sweep over the tightest reachable pairs (independent uniform hash on
// each side of a cell boundary) shows the grain term *alone* already puts
// about 5.7% of random cell-boundary samples under the 34 floor before this
// change. Adding this term at strength 0.03 / 0.04 / 0.05 moved that figure to
// 5.76% / 5.82% / 5.91% — 0.04 was picked because it costs about a tenth of a
// percentage point (roughly 2% relative) on top of the existing rate while
// still being visible at typical zoom, rather than a smaller value that would
// not read on screen or a larger one that starts compounding the margin the
// grain term already spends.
const float DETAIL_SUBCELLS = 4.0;
const float DETAIL_STRENGTH = 0.04;

float detailHash(vec2 world) {
  ivec2 p = ivec2(floor(world * DETAIL_SUBCELLS));
  uint h = uint(p.x) * 0x8da6b343u ^ uint(p.y) * 0xd8163841u;
  h ^= h >> 15; h *= 0x2c1b3c6du; h ^= h >> 12;
  return float(h & 0xffffffu) / 16777215.0;
}

// ---------------------------------------------------------------- grain
//
// **Grain is resolved from a context, not from a position.** This is the
// entire mechanism issue #121 asked for and the only thing it is used for.
//
// The world coordinate is the right key for terrain and the wrong one for a
// rigid body: a crate's wood grain hashed on where the crate *is* swims when
// the crate turns (#42 snag 2). So the resolver is handed both coordinates the
// cell has — its world cell, and the body-local window it may fall inside —
// and prefers the body's. The body's byte was keyed on the pixel's *unrotated*
// offset at bake time (BakedShape.grain in sim/rigidShapes.ts), so it is the
// same byte at all 32 orientations and the speckle turns with the crate.
//
// There is no trigonometry here and no ownership map. Both were spent in
// RigidBodies.place, where the mask walk already knew the answer.
float grainAt(ivec2 chunkCell, ivec2 worldCell) {
  for (int b = 0; b < uBodyCount; b++) {
    ivec2 d = worldCell - ivec2(uBodyOrigin[b]);
    if (d.x < 0 || d.y < 0 || d.x >= ${GRAIN_SPAN} || d.y >= ${GRAIN_SPAN}) continue;
    uint g = texelFetch(uBodyGrain, ivec2(d.x + b * ${GRAIN_SPAN}, d.y), 0).r;
    // 0 is "no pixel here" — a body window is mostly empty, so a cell inside
    // one is usually still terrain and must keep the world hash.
    if (g != 0u) return (float(g) - 1.0) / 254.0;
  }
  return hash(chunkCell);
}

// Cheap blackbody-ish ramp. Not physically accurate; tuned so that the
// temperature at which things ignite is visibly, unmistakably hot.
vec3 blackbody(float t) {
  float k = clamp((t - 180.0) / 1400.0, 0.0, 1.0);
  vec3 c = vec3(1.0, 0.28 + 0.55 * k, 0.05 + 0.55 * k * k);
  return c * k;
}

void main() {
  ivec2 cell = ivec2(floor(vCell));
  if (cell.x < 0 || cell.y < 0 || cell.x >= ${CHUNK_SIZE} || cell.y >= ${CHUNK_SIZE}) discard;

  uint id = texelFetch(uMat, cell, 0).r;
  if (id == 0u) discard;                     // VOID: let the background show

  vec4 base = texelFetch(uPalette, ivec2(int(id), 0), 0);
  vec4 props = texelFetch(uMatProps, ivec2(int(id), 0), 0);
  float grain = props.r;
  float emissive = props.g;

  // Per-cell value noise, stable in world space so terrain doesn't shimmer
  // when the camera moves — and stable in *body* space for a rigid body's
  // pixels, so a rotating crate doesn't shimmer either. See grainAt above.
  float n = grainAt(cell, ivec2(uChunkOrigin) + cell);
  base.rgb *= 1.0 + (n - 0.5) * grain * 2.0;

  // Sub-cell detail (#149). Deliberately not scaled by the per-material
  // grain property: a zero-grain material (glass, void-adjacent stuff)
  // should still pick up a hint of texture rather than reading as a dead
  // flat swatch beside its textured neighbours, and DETAIL_STRENGTH is
  // already small enough that materials with high grain are not pushed
  // meaningfully further past what the margin sweep above measured.
  float d = detailHash(vWorld);
  base.rgb *= 1.0 + (d - 0.5) * DETAIL_STRENGTH * 2.0;

  int temp = texelFetch(uTemp, cell, 0).r;
  uint flags = texelFetch(uFlags, cell, 0).r;

  bool burning  = (flags & 4u) != 0u;
  bool wet      = (flags & 2u) != 0u;
  bool charged  = (flags & 8u) != 0u;
  bool frozen   = (flags & 16u) != 0u;
  bool strained = (flags & 512u) != 0u;   // Flag.PRESSURIZED

  vec3 col = base.rgb;

  // Wetness darkens and saturates — the visual half of a mechanic that
  // players must be able to read at a glance (see docs/06 §4).
  if (wet) col = mix(col, col * vec3(0.62, 0.70, 0.92), 0.55);
  if (frozen) col = mix(col, vec3(0.72, 0.86, 0.98), 0.35);

  // Heat glow. Emissive materials always glow; anything hot enough glows too.
  vec3 glow = blackbody(float(temp)) * max(emissive, 0.35);
  if (burning) {
    float flick = 0.7 + 0.3 * sin(uTime * 22.0 + n * 40.0);
    glow += vec3(1.0, 0.5, 0.15) * flick * 0.9;
  }
  if (charged) {
    glow += vec3(0.55, 0.8, 1.0) * (0.6 + 0.4 * sin(uTime * 60.0 + n * 20.0));
  }

  // Strain telegraph. The pressure system requires a volume to be visibly
  // PRESSURIZED for a whole prior evaluation before a wall may fail, so that a
  // blowout always has a warning frame (docs/06 §4 calls an unannounced one the
  // least readable outcome in the game). That guarantee is only worth something
  // if the flag is drawn.
  //
  // Deliberately slower and warmer than the charge strobe — 7 Hz rather than
  // 60, and a rising amber rather than a cyan flicker — because the two mean
  // opposite things: charge is "this is happening now", strain is "this is
  // about to". The per-cell hash offset keeps a straining wall from pulsing as
  // one flat slab.
  if (strained) {
    float pulse = 0.5 + 0.5 * sin(uTime * 7.0 + n * 6.283);
    col = mix(col, vec3(1.0, 0.72, 0.30), 0.18 + 0.22 * pulse);
    glow += vec3(0.5, 0.26, 0.06) * pulse * 0.55;
  }

  // Incident light multiplies the material's own colour; glow is added after
  // and is never attenuated. A fire is not lit by the room, it lights it — and
  // if the emissive ramp went through the light term, lowering ambient to zero
  // would black out the one thing on screen that is definitionally visible.
  vec3 incident = sampleLight(vWorld);
  col = col * uGrade * (uAmbient + incident * uLightGain) + glow;
  outColor = vec4(col, base.a);
}`;

interface ChunkGpu {
  matTex: WebGLTexture;
  tempTex: WebGLTexture;
  flagsTex: WebGLTexture;
  /** Bumped each upload so we can evict least-recently-used GPU chunks. */
  lastUsed: number;
}

export class TerrainRenderer {
  private glc: GLContext;
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private uni: Uniforms;

  private paletteTex: WebGLTexture;
  private propsTex: WebGLTexture;
  private unlitTex: WebGLTexture;
  private gpuChunks = new Map<number, ChunkGpu>();

  /**
   * `MAX_GRAIN_SLOTS` body grain windows side by side, R8UI. 5000 bytes.
   *
   * One texture rather than one per body so a frame binds once and the
   * fragment's lookup is an offset into a row it is already sampling.
   */
  private bodyGrainTex: WebGLTexture;
  /** Which body, and which of its versions, currently occupies each slot. */
  private grainSlotId = new Int32Array(MAX_GRAIN_SLOTS).fill(-1);
  private grainSlotVersion = new Int32Array(MAX_GRAIN_SLOTS).fill(-1);
  private grainSlots = 0;
  /** `uBodyOrigin[k]` — built once, because a template string per frame is garbage. */
  private static readonly ORIGIN_UNIFORMS = Array.from(
    { length: MAX_GRAIN_SLOTS },
    (_, k) => `uBodyOrigin[${k}]`,
  );

  /** Staging buffers for sub-rectangle uploads; sized to a full chunk row-run. */
  private stageMat = new Uint16Array(CHUNK_SIZE * CHUNK_SIZE);
  private stageTemp = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
  private stageFlags = new Uint16Array(CHUNK_SIZE * CHUNK_SIZE);

  /**
   * Draw list, held across frames.
   *
   * A fresh array plus a two-element tuple per visible chunk, every frame, is
   * exactly the kind of steady-state garbage the rest of the pipeline goes out
   * of its way not to produce.
   */
  private visibleChunks: Chunk[] = [];
  private visibleGpu: ChunkGpu[] = [];
  private visibleCount = 0;

  /** Biome colour grade, lerped by the game when biomes change. */
  grade = { r: 1, g: 1, b: 1 };

  private frame = 0;

  constructor(glc: GLContext) {
    this.glc = glc;
    this.gl = glc.gl;
    this.prog = createProgram(this.gl, VERT, FRAG, 'terrain');
    this.uni = new Uniforms(this.gl, this.prog);
    this.paletteTex = this.buildPalette();
    this.propsTex = this.buildProps();
    this.unlitTex = createUnlitTexture(this.gl);
    this.bodyGrainTex = this.buildBodyGrain();
  }

  /** Storage for the body grain windows. Contents arrive per frame. */
  private buildBodyGrain(): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.R8UI, GRAIN_SPAN * MAX_GRAIN_SLOTS, GRAIN_SPAN);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /**
   * Fill the grain slots from the solver, and say how many the shader may read.
   *
   * **Two things keep this off the frame budget, and both are measured rather
   * than asserted** (`tests/rigidGrain.test.ts`, `tests/gameConstruction.test.ts`):
   *
   *   - A slot is re-uploaded only when the body in it changed pose. A
   *     sleeping crate is placed once and then costs **zero bytes a frame**,
   *     which is the same claim `sim/rigid.ts` makes about its cell uploads
   *     and for the same reason — nothing changed.
   *   - A moving body costs `GRAIN_SPAN²` = 625 bytes. Four of them is 2.5 KB
   *     against the ~49 KB of cell words those same four bodies dirty, so the
   *     grain is about 5 % of what the bodies already cost to draw.
   *
   * Windows outside the view are skipped before they can take a slot, so a
   * distant sleeping crate does not spend one.
   */
  private uploadBodyGrain(source: GrainSource | undefined, view: {
    x0: number; y0: number; x1: number; y1: number;
  }): void {
    if (source === undefined) {
      this.grainSlots = 0;
      return;
    }
    const gl = this.gl;
    const windows = source.grainWindows();
    let slot = 0;
    gl.bindTexture(gl.TEXTURE_2D, this.bodyGrainTex);
    for (let i = 0; i < windows.length && slot < MAX_GRAIN_SLOTS; i++) {
      const g = windows[i];
      if (g.ox + GRAIN_SPAN < view.x0 || g.ox > view.x1) continue;
      if (g.oy + GRAIN_SPAN < view.y0 || g.oy > view.y1) continue;
      if (this.grainSlotId[slot] !== g.id || this.grainSlotVersion[slot] !== g.version) {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, slot * GRAIN_SPAN, 0, GRAIN_SPAN, GRAIN_SPAN,
          gl.RED_INTEGER, gl.UNSIGNED_BYTE, g.data, 0);
        this.grainSlotId[slot] = g.id;
        this.grainSlotVersion[slot] = g.version;
        profiler.count('bodyGrainBytes', g.data.length);
      }
      this.uni.f2(TerrainRenderer.ORIGIN_UNIFORMS[slot], g.ox, g.oy);
      slot++;
    }
    this.grainSlots = slot;
  }

  /** RGBA8 palette straight from the material registry's flattened bytes. */
  private buildPalette(): WebGLTexture {
    const gl = this.gl;
    const n = materialCount();
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, Math.max(1, n), 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, n, 1, gl.RGBA, gl.UNSIGNED_BYTE, M.palette);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /** Per-material shading properties, packed into one RGBA float row. */
  private buildProps(): WebGLTexture {
    const gl = this.gl;
    const n = materialCount();
    const data = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
      data[i * 4 + 0] = M.grain[i];
      data[i * 4 + 1] = M.emissive[i];
      data[i * 4 + 2] = M.alpha[i];
      data[i * 4 + 3] = M.opacity[i];
    }
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA32F, Math.max(1, n), 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, n, 1, gl.RGBA, gl.FLOAT, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    return tex;
  }

  private gpuFor(chunk: Chunk, key: number): ChunkGpu {
    let g = this.gpuChunks.get(key);
    if (g) return g;
    const gl = this.gl;

    const mk = (internal: number) => {
      const t = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texStorage2D(gl.TEXTURE_2D, 1, internal, CHUNK_SIZE, CHUNK_SIZE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return t;
    };

    g = {
      matTex: mk(gl.R16UI),
      tempTex: mk(gl.R16I),
      flagsTex: mk(gl.R16UI),
      lastUsed: this.frame,
    };
    this.gpuChunks.set(key, g);
    // A fresh GPU chunk has no data, so force a full upload regardless of the
    // chunk's own dirty state.
    chunk.markRenderAll();
    return g;
  }

  /**
   * Upload only the dirty tiles.
   *
   * The chunk tracks change at 16×16-tile granularity rather than as a bounding
   * box (see `world.ts`). A box was the wrong shape for the loudest case in the
   * game: `thermal.react` touches burning cells scattered across a chunk, the
   * box grew to the full 128×128, and the frame uploaded 16384 cells × 3
   * textures — 1.57 MB/frame, 94 MB/s — when a few hundred cells had changed.
   *
   * Horizontally adjacent dirty tiles in the same tile-row merge into one
   * `texSubImage2D` run, so the call count stays in single digits per chunk
   * while the bytes track what actually changed. Bandwidth and call count pull
   * in opposite directions; tile-runs is the knee.
   */
  private upload(chunk: Chunk, g: ChunkGpu): void {
    if (!chunk.renderDirty) return;
    const gl = this.gl;
    const tiles = chunk.renderTiles;
    const mat = chunk.mat;
    const temp = chunk.temp;
    const flags = chunk.flags;
    const stageMat = this.stageMat;
    const stageTemp = this.stageTemp;
    const stageFlags = this.stageFlags;
    let uploaded = 0;

    for (let ty = 0; ty < RENDER_TILES_PER_AXIS; ty++) {
      const rowBase = ty << RENDER_TILE_AXIS_SHIFT;
      let tx = 0;
      while (tx < RENDER_TILES_PER_AXIS) {
        if (tiles[rowBase + tx] === 0) {
          tx++;
          continue;
        }
        let tx1 = tx;
        while (tx1 + 1 < RENDER_TILES_PER_AXIS && tiles[rowBase + tx1 + 1] !== 0) tx1++;

        const x0 = tx * RENDER_TILE_SIZE;
        const y0 = ty * RENDER_TILE_SIZE;
        const w = (tx1 - tx + 1) * RENDER_TILE_SIZE;

        // Pack the run into a contiguous staging buffer. `subarray` would read
        // better but allocates three TypedArray views per row per chunk — up to
        // 6144 objects a frame with 16 dirty chunks, which is the single
        // hottest allocation site in the renderer.
        let o = 0;
        for (let y = 0; y < RENDER_TILE_SIZE; y++) {
          const src = ((y0 + y) << CHUNK_SHIFT) + x0;
          for (let k = 0; k < w; k++) {
            stageMat[o + k] = mat[src + k];
            stageTemp[o + k] = temp[src + k];
            stageFlags[o + k] = flags[src + k];
          }
          o += w;
        }

        // The `srcOffset` overload takes the whole buffer and an element
        // offset, so no `subarray` view is needed to bound the upload either.
        gl.bindTexture(gl.TEXTURE_2D, g.matTex);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, x0, y0, w, RENDER_TILE_SIZE,
          gl.RED_INTEGER, gl.UNSIGNED_SHORT, stageMat, 0);
        gl.bindTexture(gl.TEXTURE_2D, g.tempTex);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, x0, y0, w, RENDER_TILE_SIZE,
          gl.RED_INTEGER, gl.SHORT, stageTemp, 0);
        gl.bindTexture(gl.TEXTURE_2D, g.flagsTex);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, x0, y0, w, RENDER_TILE_SIZE,
          gl.RED_INTEGER, gl.UNSIGNED_SHORT, stageFlags, 0);

        uploaded += o;
        tx = tx1 + 1;
      }
    }

    if (uploaded > 0) profiler.count('cellsUploaded', uploaded);
    chunk.clearRenderDirty();
  }

  /**
   * `light` is optional so the field can land without a coordinated change to
   * the frame loop: omitted, the shader runs at ambient 1 and gain 0, which is
   * bit-for-bit the image this renderer produced before §6.3 existed.
   */
  render(
    cells: CellWorld,
    camera: Camera,
    time: number,
    light?: LightTexture,
    bodies?: GrainSource,
  ): void {
    profiler.begin('upload');
    const gl = this.gl;
    this.frame++;

    const view = camera.visibleRect(CHUNK_SIZE);
    const cx0 = Math.floor(view.x0) >> CHUNK_SHIFT;
    const cx1 = Math.floor(view.x1) >> CHUNK_SHIFT;
    const cy0 = Math.floor(view.y0) >> CHUNK_SHIFT;
    const cy1 = Math.floor(view.y1) >> CHUNK_SHIFT;

    // Upload pass first, so all texture state churn is in one place. Pinned to
    // unit 0 because `upload` binds chunk textures on whatever unit happens to
    // be active, and the draw loop rebinds 0..2 for every chunk anyway — the
    // units that must survive this pass (3..6) are the ones it must not touch.
    gl.activeTexture(gl.TEXTURE0);
    let n = 0;
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const chunk = cells.peekChunk(cx, cy);
        if (chunk === null || chunk.nonEmpty === 0) continue;
        const key = ((cx + 32768) << 16) | (cy + 32768);
        const g = this.gpuFor(chunk, key);
        g.lastUsed = this.frame;
        this.upload(chunk, g);
        this.visibleChunks[n] = chunk;
        this.visibleGpu[n] = g;
        n++;
      }
    }
    this.visibleCount = n;
    // Drop references past the live prefix. Shrinking a JS array does not
    // reallocate, but holding a stale `Chunk` here would keep an evicted chunk
    // and its four typed arrays alive indefinitely.
    if (this.visibleChunks.length > n) {
      this.visibleChunks.length = n;
      this.visibleGpu.length = n;
    }
    profiler.end('upload');

    profiler.begin('render');
    gl.useProgram(this.prog);
    gl.bindVertexArray(this.glc.quad());
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, this.propsTex);
    if (light) {
      light.bind(5);
      this.uni.f2('uLightOrigin', light.originX, light.originY);
      this.uni.f2('uLightScale', light.scaleX, light.scaleY);
      this.uni.f2('uLightTexSize', light.width, light.height);
      this.uni.f1('uAmbient', light.ambient);
      this.uni.f1('uLightGain', light.gain);
    } else {
      gl.activeTexture(gl.TEXTURE5);
      gl.bindTexture(gl.TEXTURE_2D, this.unlitTex);
      this.uni.f2('uLightOrigin', 0, 0);
      this.uni.f2('uLightScale', 0, 0);
      // 1×1, matching `createUnlitTexture` — never 0, which would divide by
      // zero in `sampleLight` and NaN-poison `col` even though `uLightGain`
      // is 0 (NaN * 0 is NaN, not 0).
      this.uni.f2('uLightTexSize', 1, 1);
      this.uni.f1('uAmbient', 1);
      this.uni.f1('uLightGain', 0);
    }

    // After `useProgram`: the slot origins are uniforms of this program.
    gl.activeTexture(gl.TEXTURE6);
    gl.bindTexture(gl.TEXTURE_2D, this.bodyGrainTex);
    this.uploadBodyGrain(bodies, view);

    this.uni.i1('uMat', 0);
    this.uni.i1('uTemp', 1);
    this.uni.i1('uFlags', 2);
    this.uni.i1('uPalette', 3);
    this.uni.i1('uMatProps', 4);
    this.uni.i1('uLight', 5);
    this.uni.i1('uBodyGrain', 6);
    this.uni.i1('uBodyCount', this.grainSlots);
    this.uni.f1('uTime', time);
    this.uni.f2('uCamera', camera.renderX, camera.renderY);
    this.uni.f2('uViewSize', camera.viewW, camera.viewH);
    this.uni.f3('uGrade', this.grade.r, this.grade.g, this.grade.b);
    this.uni.f1('uLodPressure', profiler.lodPressure);

    for (let k = 0; k < this.visibleCount; k++) {
      const chunk = this.visibleChunks[k];
      const g = this.visibleGpu[k];
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, g.matTex);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, g.tempTex);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, g.flagsTex);
      this.uni.f2('uChunkOrigin', chunk.cx << CHUNK_SHIFT, chunk.cy << CHUNK_SHIFT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      profiler.count('drawCalls');
    }

    gl.bindVertexArray(null);
    profiler.setCount('terrainChunks', this.visibleCount);
    profiler.end('render');

    if (this.gpuChunks.size > 96) this.evict();
  }

  /** Drop GPU textures for chunks we haven't drawn in a while. */
  private evict(): void {
    const gl = this.gl;
    for (const [key, g] of this.gpuChunks) {
      if (this.frame - g.lastUsed > 240) {
        gl.deleteTexture(g.matTex);
        gl.deleteTexture(g.tempTex);
        gl.deleteTexture(g.flagsTex);
        this.gpuChunks.delete(key);
      }
    }
  }

  /** Rebuild the palette — call after a biome swap changes material colours. */
  refreshPalette(): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, materialCount(), 1, gl.RGBA,
      gl.UNSIGNED_BYTE, M.palette);
  }
}
